import { useCallback, useEffect, useState } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import {
  NoPlaceZoneBooking,
  ServiceDeliveryStatus,
  ServicesZoneBooking
} from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { useRemoteFetchAllCateringServices } from "../../../../hooks/Remote/Services/CateringMenu/useRemoteFetchAllCateringServices";
import { useRemoteFetchBookableEquipments } from "../../../../hooks/Remote/BookableEquipment/useRemoteFetchBookableEquipment";
import { useRemoteFetchRoomlayout } from "../../../../hooks/Remote/Services/Roomlayout/useRemoteFetchRoomlayout";
import {
  mapAssignedServiceData,
  mapCateringData,
  mapEquipmentData,
  mapRoomLayoutData,
  storeUserInfoData,
  updateUnorderedServiceData
} from "./functions/mapServiceData.functions";
import { useFeatures } from "../../../FacilityManager/Hooks/useFeatures";
import {
  UpdateServicesDto,
  useRemoteUpdateBookingServicesByUser
} from "../../../../hooks/Remote/Services/OrderRequests/useRemoteUpdateBookingServicesByUser";
import { BookingType } from "../../../../features/Booking-Form/typings/booking-inputs";
import { useSnackbar } from "notistack";
import { handleValidationError } from "./functions/handleParseValidationError.function";
import { ScheduleSelectionServicesContents } from "./ScheduleSelection-Tab-Services-Contents.component";
import DefaultSpinner from "../../../LoadingSpinner/default-spinner.component";
import { useRemoteFetchFloorPlanService } from "../../../../hooks/Remote/FloorPlan/useRemoteFetchFloorPlanService";
import { useRemoteFetchMaxSetupTime } from "../../../../hooks/Remote/Services/BookingServices/useRemoteFetchMaxSetupTime";
import { transformOrderRequest } from "../../../ServiceManagement/OrderRequests/functions/transformOrderRequest.function";
import { Colleague } from "../../../../features/Connections/types/Colleague.type";
import { IPlaceInventory } from "../../../FacilityManager/Domain/Types/FloorPlan/PlaceInventory.type";
import { IZoneInventory } from "../../../FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";
import { resolveLocationInventoryIdFromSchedule } from "../../../FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/SelectionPrompt.functions";
import { useIsUserServiceAdminForLocation } from "../../../../hooks/Remote/Admin/useIsUserServiceAdminForLocation";
import { useRemoteFetchPermissionContext } from "../../../../hooks/Remote/User/UserInfo/useRemoteFetchPermissionContext";

export type ServiceData = Omit<ServicesZoneBooking, "booking"> & {
  name?: string;
  pricePerUnit?: number;
  picturePath?: string | null; // single
  picturePaths?: (string | null)[] | null; // or mulitple
  ordered?: boolean; // added to edit
  editable?: boolean;
  permissions?: {
    id?: number;
    companyIds: string[];
    costCenterUnitIds: string[];
    userIds: string[];
  };
};

type P = {
  schedule: NoPlaceZoneBooking;
  bookingUser: Colleague | undefined;
  isMobileTablet: boolean;
  /** Own upcoming editability only — company-admin past/running does not unlock services (#1577). */
  isEditable: boolean;
  refetchAllSchedule?: () => void;
};

export const ScheduleSelectionServicesTab: React.FC<P> = ({
  schedule,
  bookingUser,
  isMobileTablet,
  isEditable,
  refetchAllSchedule
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { useShowBookingServiceUpdateDesc } = useFeatures();
  const { sub: userId } = useSelector((state: RootState) => state.login.userInformation);
  const isRequestAdmin = useSelector((state: RootState) => state.login.userRoles.isRequestAdmin);

  /** Calendar dialog does not use full booking-form Redux state; use schedule + inventory fallbacks only. */
  const locationInventoryIdForServiceAdmin = resolveLocationInventoryIdFromSchedule(schedule);

  const { isLoading: isServiceAdminLoading, data: isServiceAdminForLocation } =
    useIsUserServiceAdminForLocation(locationInventoryIdForServiceAdmin, userId, isRequestAdmin);

  const isServiceAdminBypass = isServiceAdminForLocation === true;
  /** Own upcoming only — not company-admin past/running (#1577). */
  const canEditServices = isEditable;
  const canStartServiceDataLoad = !isServiceAdminLoading;

  const { serviceBookings } = schedule;

  const { data: remoteCateringMenu } = useRemoteFetchAllCateringServices(
    locationInventoryIdForServiceAdmin
  );
  const { data: remoteBookableEquipment } = useRemoteFetchBookableEquipments();
  const { data: remoteRoomLayout } = useRemoteFetchRoomlayout();

  const { data: remoteMaxSetupTime } = useRemoteFetchMaxSetupTime(
    {
      bookingInventoryIds: [schedule.bookingInventoryId],
      startDate: schedule.startDate,
      endDate: schedule.endDate,
      startTime: DateTime.fromISO(schedule.startDate).toFormat("HH:mm"),
      endTime: DateTime.fromISO(schedule.endDate).toFormat("HH:mm"),
      bookingType: BookingType.CONFERENCEZONE,
      timezone: schedule.timezone,
      weekdays: undefined,
      monday: "no use"
    },
    true
  );

  const { data: permissionContextData } = useRemoteFetchPermissionContext(false); // currently not used
  const permissionContext = permissionContextData?.[0];

  const { mutateAsync: postOrderRequest } = useRemoteUpdateBookingServicesByUser();

  // to find the assigned service items in the service plan
  const { data: floorPlanForService } = useRemoteFetchFloorPlanService(
    (schedule.bookingInventory as IPlaceInventory | IZoneInventory)?.floorInventoryId || 0,
    schedule.start || schedule.startDate,
    schedule.end || schedule.endDate,
    !!schedule
  );

  const [cateringData, setCateringData] = useState<ServiceData[]>([]);
  const [equipmentData, setEquipmentData] = useState<ServiceData[]>([]);
  const [layoutData, setLayoutData] = useState<ServiceData[]>([]);
  const [selectCostCenter, setSelectCostCenter] = useState<{ id: string; name: string } | null>(
    null
  );

  const [isLoaded, setIsLoaded] = useState(false);

  const createInitialDataInitState = () => ({
    catering: false,
    equipment: false,
    roomLayout: false
  });
  const [dataInitState, setDataInitState] = useState(createInitialDataInitState);
  const markDataInitialized = useCallback(
    (key: keyof ReturnType<typeof createInitialDataInitState>) => {
      setDataInitState(prev => (prev[key] ? prev : { ...prev, [key]: true }));
    },
    []
  );

  const tabConfigs = [
    {
      label: "Catering",
      index: 0,
      count: cateringData.filter(cat => cat.ordered).length,
      orderable: cateringData.filter(cat => cat.editable).length,
      testId: "booking-services-selection-tab0"
    },
    {
      label: "Equipment",
      index: 1,
      count: equipmentData.filter(eq => eq.ordered).length,
      orderable: equipmentData.filter(eq => eq.editable).length,
      testId: "booking-services-selection-tab1"
    },
    {
      label: "Room Layout",
      index: 2,
      count: layoutData.filter(layout => layout.ordered).length,
      orderable: layoutData.filter(layout => layout.editable).length,
      testId: "booking-services-selection-tab2"
    }
  ];
  // Filter out any tab with no count
  const visibleTabs = tabConfigs.filter(tab => tab.orderable > 0 || tab.count > 0);

  const renameSetupTime = (data: ServiceData[]) =>
    data.map(item => ({
      ...item,
      setupTime: item.setUpTime
    }));

  const handlePostOrderRequest = async (updated: UpdateServicesDto[]) => {
    await postOrderRequest(updated)
      .then(() => {
        enqueueSnackbar(t("Successfully edited booking"), { variant: "success" });
        refetchAllSchedule?.();
      })
      .catch((err: any) => {
        handleValidationError({
          cateringData,
          equipmentData,
          layoutData,
          errorMessage: err.response.data.message,
          enqueueSnackbar,
          t
        });
      });
  };

  // update the booking services order like catering menu, bookable equipments, layout
  async function handleConfirmservice() {
    const changes = {
      caterings: renameSetupTime(cateringData).filter(item => item.ordered) as any,
      equipments: renameSetupTime(equipmentData).filter(item => item.ordered) as any,
      roomLayouts: renameSetupTime(layoutData).filter(item => item.ordered) as any
    };

    const updated = transformOrderRequest(
      schedule.id.toString(), // targetBookingId
      schedule.serviceBookings?.[0]?.deliveryStatus ?? ServiceDeliveryStatus.ORDERED,
      schedule.bookingType as BookingType, // detailData.placeOrZoneType
      changes
    );

    handlePostOrderRequest(updated);
  }

  // catering data state
  useEffect(() => {
    if (!remoteCateringMenu || !remoteMaxSetupTime) return;
    if (!canStartServiceDataLoad) return;
    if (isLoaded) return;

    mapCateringData({
      serviceBookings,
      remoteCateringMenu,
      startDate: schedule.startDate,
      maxSetUpTime: remoteMaxSetupTime.maxSetUpTime,
      setCateringData,
      isServiceAdminBypass,
      isEditable: canEditServices
    });
    markDataInitialized("catering");
  }, [
    schedule,
    remoteCateringMenu,
    remoteMaxSetupTime,
    isLoaded,
    canStartServiceDataLoad,
    isServiceAdminBypass,
    canEditServices
  ]);

  // bookable equipment data state
  useEffect(() => {
    if (!remoteBookableEquipment || !remoteMaxSetupTime) return;
    if (!canStartServiceDataLoad) return;
    if (isLoaded) return;

    mapEquipmentData({
      serviceBookings,
      remoteBookableEquipment,
      startDate: schedule.startDate,
      maxSetUpTime: remoteMaxSetupTime.maxSetUpTime,
      setEquipmentData,
      isServiceAdminBypass,
      isEditable: canEditServices
    });
    markDataInitialized("equipment");
  }, [
    schedule,
    remoteBookableEquipment,
    remoteMaxSetupTime,
    isLoaded,
    canStartServiceDataLoad,
    isServiceAdminBypass,
    canEditServices
  ]);

  // room layout data state
  useEffect(() => {
    if (!remoteRoomLayout || !remoteMaxSetupTime) return;
    if (!canStartServiceDataLoad) return;
    if (isLoaded) return;

    mapRoomLayoutData({
      serviceBookings,
      remoteRoomLayout,
      startDate: schedule.startDate,
      maxSetUpTime: remoteMaxSetupTime.maxSetUpTime,
      setLayoutData,
      isServiceAdminBypass,
      isEditable: canEditServices
    });
    markDataInitialized("roomLayout");
  }, [
    schedule,
    remoteRoomLayout,
    remoteMaxSetupTime,
    isLoaded,
    canStartServiceDataLoad,
    isServiceAdminBypass,
    canEditServices
  ]);

  // cost center state
  useEffect(() => {
    storeUserInfoData({ permissionContext, setSelectCostCenter });
  }, [schedule, permissionContext]);

  // floor plan for service to look for the assigned service items
  useEffect(() => {
    if (isLoaded) return;
    if (!canStartServiceDataLoad) return;
    if (!floorPlanForService || !remoteMaxSetupTime?.maxSetUpTime) return;

    // when it's not editable, don't need to load unordered service data
    if (!canEditServices) {
      setIsLoaded(true);
      return;
    }

    const allDataInitialized = Object.values(dataInitState).every(value => value === true);
    if (allDataInitialized) {
      /* istanbul ignore next */
      const { caterings, bookEquipments, layouts } = mapAssignedServiceData({
        bookingInventoryId: schedule.bookingInventoryId,
        floorPlanForService,
        serviceBookings,
        remoteCateringMenu,
        remoteBookableEquipment,
        remoteRoomLayout
      });

      // include unordered service data filtered by service time
      updateUnorderedServiceData({
        serviceData: { caterings, bookEquipments, layouts },
        startDate: schedule.startDate,
        maxSetupTime: remoteMaxSetupTime?.maxSetUpTime,
        bookingUserInfo: {
          userId: bookingUser?.userId ?? schedule.userBookedFor,
          costCenterId: bookingUser?.departmentId ?? "",
          companyId: bookingUser?.companyId ?? 0
        },
        updateAction: { setCateringData, setEquipmentData, setLayoutData },
        isServiceAdminBypass
      });

      // complete the load
      setIsLoaded(true);
    }
  }, [
    floorPlanForService,
    remoteMaxSetupTime,
    dataInitState,
    isLoaded,
    canStartServiceDataLoad,
    isServiceAdminBypass,
    canEditServices,
    schedule,
    serviceBookings,
    remoteCateringMenu,
    remoteBookableEquipment,
    remoteRoomLayout,
    bookingUser
  ]);

  return (
    <Grid2
      container
      data-testid="booking-selection-tabPanel-2-services"
      rowGap={isMobileTablet ? 2 : 5}
      sx={[
        isMobileTablet ? { width: "99%" } : { width: "31vw" },
        isMobileTablet ? { mt: 0 } : { mt: 2 }
      ]}
    >
      {/* desciption of booking service changes - not used */}
      {useShowBookingServiceUpdateDesc && (
        <Grid2
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "95%",
            pl: 1.5,
            border: "0.5px solid",
            borderRadius: 2
          }}
        >
          <InfoOutlined />
          <Box sx={{ p: 1 }}>
            <Typography variant={"body2"}>{t("_descriptionOfBookingServiceChanges")}</Typography>
          </Box>
        </Grid2>
      )}

      {/* contents of booking service */}
      {isLoaded && (
        <ScheduleSelectionServicesContents
          schedule={schedule}
          isMobileTablet={isMobileTablet}
          isEditable={isEditable}
          cateringData={cateringData}
          equipmentData={equipmentData}
          layoutData={layoutData}
          selectCostCenter={selectCostCenter}
          visibleTabs={visibleTabs}
          remoteMaxSetupTime={remoteMaxSetupTime}
          handleConfirmservice={handleConfirmservice}
          setCateringData={setCateringData}
          setSelectCostCenter={setSelectCostCenter}
          setEquipmentData={setEquipmentData}
          setLayoutData={setLayoutData}
        />
      )}

      {!isLoaded && (
        <Grid2 sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
          <DefaultSpinner />
        </Grid2>
      )}
    </Grid2>
  );
};
